import { menuPagePath } from '@/domain/catalog/menu-links';
import type {
  IStoreSetupLastUpdate,
  IStoreSetupStatus,
} from '@/interfaces/IStoreSetup';
import { menuTarget } from './setup-flow';
import type { IChecklistRow, TChecklistBand } from './setup-flow';

/**
 * Pure rules of the restaurant dashboard's setup parts (plan §4.10): when
 * the checklist replaces the old onboarding card, its progress and bands,
 * the «Su menú» card and the teaching copy of empty metrics.
 */

/** Flow stores (a setup row) show the checklist until the owner hides it. */
export function isChecklistVisible(status: IStoreSetupStatus | null): boolean {
  return !!status?.setup && !status.setup.checklistHiddenAt;
}

export interface IChecklistProgress {
  done: number;
  total: number;
  allDone: boolean;
}

export function checklistProgress(
  rows: readonly IChecklistRow[]
): IChecklistProgress {
  const done = rows.filter((row) => row.done).length;
  return {
    done,
    total: rows.length,
    allDone: rows.length > 0 && done === rows.length,
  };
}

export interface IChecklistBandGroup {
  band: TChecklistBand;
  titleKey: string;
  rows: IChecklistRow[];
}

const BAND_TITLE_KEYS: Readonly<Record<TChecklistBand, string>> = {
  orders: 'storeSetup.checklist.bandOrders',
  more: 'storeSetup.checklist.bandMore',
};

/** Consecutive rows of the same band under one band title, in row order. */
export function checklistBands(
  rows: readonly IChecklistRow[]
): IChecklistBandGroup[] {
  const groups: IChecklistBandGroup[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.band === row.band) last.rows.push(row);
    else
      groups.push({
        band: row.band,
        titleKey: BAND_TITLE_KEYS[row.band],
        rows: [row],
      });
  }
  return groups;
}

/** Width of the progress bar: the done share of the rows, 0–100. */
export function progressPercent(done: number, total: number): number {
  const share = total > 0 ? Math.min(Math.max(done / total, 0), 1) : 0;
  return share * 100;
}

// ---------------------------------------------------------------------------
// «Su menú» card
// ---------------------------------------------------------------------------

export interface IMenuCardAction {
  labelKey: string;
  to: string;
}

export type TMenuCardModel =
  | { state: 'reading'; action: IMenuCardAction }
  | { state: 'draft'; action: IMenuCardAction }
  | {
      state: 'menu';
      visibleItems: number;
      categories: number;
      hiddenItems: number;
      editTo: string;
      /** «Actualizar desde fotos»; null for stores with several menus (§4.11). */
      loadMoreTo: string | null;
      /** The latest update from photos that can still be undone («Actualizado con fotos {when} · Deshacer»). */
      lastUpdate: IStoreSetupLastUpdate | null;
    }
  | { state: 'empty'; action: IMenuCardAction };

/**
 * A running read, then a draft with dishes, then the published menu, else
 * the empty state. Targets as in the checklist's menu row; stores with
 * several menus cannot use the AI import (§4.11), so their empty state
 * opens the items page instead.
 */
export function menuCardModel(
  status: IStoreSetupStatus,
  stringId: string,
  multipleMenus: boolean,
  /** The Menú page is on for the store (plan §5.1). */
  menuV2 = false
): TMenuCardModel {
  const session = status.menu.activeSession;
  const target = menuTarget(status, stringId);
  if (session?.status === 'reading')
    return {
      state: 'reading',
      action: { labelKey: 'storeSetup.dashboard.see', to: target },
    };
  if (session && session.items > 0)
    return {
      state: 'draft',
      action: { labelKey: 'storeSetup.dashboard.review', to: target },
    };
  if (status.menu.visibleItems > 0)
    return {
      state: 'menu',
      visibleItems: status.menu.visibleItems,
      categories: status.menu.categories,
      hiddenItems: status.menu.hiddenItems,
      editTo: menuPagePath(stringId, menuV2),
      loadMoreTo: multipleMenus ? null : `/${stringId}/menu/import`,
      lastUpdate: status.menu.lastUpdate ?? null,
    };
  return {
    state: 'empty',
    action: {
      labelKey: 'storeSetup.dashboard.emptyAction',
      to: multipleMenus ? menuPagePath(stringId, menuV2) : target,
    },
  };
}

// ---------------------------------------------------------------------------
// Empty metrics
// ---------------------------------------------------------------------------

export interface IEmptyMetricsInput {
  /** A flow store with the checklist visible. */
  enabled: boolean;
  ordersCount: number;
  revenueAmount: number;
  viewsCount: number;
}

export interface IEmptyMetricKeys {
  orders: string | null;
  revenue: string | null;
  views: string | null;
}

/** The teaching copy of each of today's cards, only while its number is 0. */
export function emptyMetricKeys(input: IEmptyMetricsInput): IEmptyMetricKeys {
  if (!input.enabled) return { orders: null, revenue: null, views: null };
  return {
    orders: input.ordersCount === 0 ? 'storeSetup.dashboard.salesEmpty' : null,
    revenue:
      input.revenueAmount === 0 ? 'storeSetup.dashboard.salesEmpty' : null,
    views: input.viewsCount === 0 ? 'storeSetup.dashboard.viewsEmpty' : null,
  };
}
